import {
  DefaultCell,
  TableCellProps,
  createActionsColumn,
  getObjectPropFromString,
} from '@agilelab/plugin-wb-platform';
import { TableColumn, TableProps } from '@mainstage/core-components';
import {
  Icon,
  IconButton,
  Menu,
  MenuItem,
  makeStyles,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { FC, MouseEvent, useState } from 'react';
import { CatalogTableRow } from '../types';

interface Props {
  columns: TableColumn<CatalogTableRow>[];
  actions: TableProps<CatalogTableRow>['actions'];
}

interface ActionColumnProps {
  actions: TableProps<CatalogTableRow>['actions'];
  field: CatalogTableRow;
}

const useStyles = makeStyles(() => ({
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
}));

const ActionColumn: FC<ActionColumnProps> = ({ actions, field }) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton
        data-testid="catalog-actions-menu"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={handleClick}
        size="small"
      >
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={e => {
          e.stopPropagation();
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {(actions || []).map((action, index) => {
          const item =
            // eslint-disable-next-line no-nested-ternary
            typeof action === 'function'
              ? action(field)
              : 'action' in action
              ? action.action(field)
              : action;

          if (item.tooltip?.includes('favorites')) return null;

          return (
            <MenuItem
              disabled={item.disabled}
              key={index}
              className={classes.menuItem}
              onClick={e => item.onClick(e, field)}
            >
              {typeof item.icon === 'string' ? (
                <Icon>{item.icon}</Icon>
              ) : (
                item.icon(item.iconProps as any)
              )}

              {item.tooltip}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

export function useColumns({ columns, actions }: Props) {
  const sortableColumns = [
    'Name',
    'Domain',
    'Owner',
    'Description',
    'Type',
    'Dataproduct',
  ];
  const hasActions = Boolean((actions || []).length);

  const newColumns: TableCellProps<CatalogTableRow>[] = columns.map(col => ({
    field: (col.field || '') as keyof CatalogTableRow,
    fieldRender: (field: CatalogTableRow) => {
      const value = getObjectPropFromString(
        field,
        col.field || '',
        obj => obj.resolved?.name,
      )
        .toLowerCase()
        .trim();
      return col?.render?.(field, 'row') || <DefaultCell value={value} />;
    },
    headerName: col.title?.toString(),
    sortable: sortableColumns.includes(col.title?.toString() || ''),
  }));
  if (hasActions) {
    newColumns.push(
      createActionsColumn({
        renderActions: field => (
          <ActionColumn field={field} actions={actions} />
        ),
      }),
    );
  }

  return newColumns;
}
